import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import QRCode from 'qrcode'
import html2pdf from 'html2pdf.js'

interface ReportImage {
  id: number
  slide: string
  slide_name: string
  annotation_id?: number
  annotation_data?: {
    id: string
    annotation_type: string
    svg_data: string
    text_content?: string
    coordinates: any
    color: string
    stroke_width: number
    description?: string
  }
  snapshot?: string  // UUID string
  snapshot_id?: string  // UUID string
  snapshot_image_url?: string
  snapshot_thumbnail_url?: string
  order: number
}

interface CytologyReport {
  id: number
  patient: number
  patient_name: string
  patient_code: string
  age: number
  gender: string
  address: string
  province: string
  department: string
  treating_doctor: string
  clinical_diagnosis: string
  specimen_type: string
  organisms: Record<string, boolean>
  squamous_cells: Record<string, boolean>
  glandular_cells: Record<string, boolean>
  other_findings: Record<string, boolean>
  conclusion: string
  recommendation: string
  cytopathologist: string
  status: string
  report_date: string
  updated_at: string
  report_images?: ReportImage[]
}

const CytologyReportViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [report, setReport] = useState<CytologyReport | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const qrCanvasRef = useRef<HTMLCanvasElement>(null)
  const printContainerRef = useRef<HTMLDivElement>(null)

  // Load template config
  const [hospitalName, setHospitalName] = useState('SỞ Y TẾ THÁI NGUYÊN')
  const [headerText, setHeaderText] = useState('KẾT QUẢ XÉT NGHIỆM TẾ BÀO HỌC SÀNG LỌC UNG THƯ CỔ TỬ CUNG')
  const [hospitalAddress, setHospitalAddress] = useState('Thịnh Đán, Thành phố Thái Nguyên, Thái Nguyên')
  const [logoUrl, setLogoUrl] = useState('🏥')

  useEffect(() => {
    const savedConfig = localStorage.getItem('cytologyTemplateConfig')
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig)
        setHospitalName(config.hospitalName || 'SỞ Y TẾ THÁI NGUYÊN')
        setHeaderText(config.headerText || 'KẾT QUẢ XÉT NGHIỆM TẾ BÀO HỌC SÀNG LỌC UNG THƯ CỔ TỬ CUNG')
        setHospitalAddress(config.hospitalAddress || 'Thịnh Đán, Thành phố Thái Nguyên, Thái Nguyên')
        setLogoUrl(config.logoUrl || '🏥')
      } catch (error) {
        console.error('Error loading template config:', error)
      }
    }
  }, [])

  useEffect(() => {
    if (id) {
      fetchReport()
    }
  }, [id])

  // Generate QR code when report is loaded
  useEffect(() => {
    if (report && qrCanvasRef.current) {
      // QR code always links to image gallery page for this report
      const imageViewerUrl = `${window.location.origin}/pathology/cytology-reports/${report.id}/images`

      QRCode.toCanvas(qrCanvasRef.current, imageViewerUrl, {
        width: 128,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      }).catch((err: Error) => {
        console.error('Error generating QR code:', err)
      })
    }
  }, [report])

  // Removed: No approval workflow - reports are auto-finalized

  const fetchReport = async () => {
    try {
      setIsLoading(true)
      const response = await fetch(`/api/v1/pathology/cytology-reports/${id}/`)
      if (response.ok) {
        const data = await response.json()
        setReport(data)
      } else {
        toast.error('Không thể tải phiếu kết quả')
        navigate('/pathology/reports')
      }
    } catch (error) {
      console.error('Error fetching report:', error)
      toast.error('Lỗi khi tải phiếu kết quả')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      draft: { label: 'Nháp', color: 'bg-yellow-100 text-yellow-800' },
      finalized: { label: 'Đã duyệt', color: 'bg-green-100 text-green-800' },
      pending: { label: 'Chờ duyệt', color: 'bg-blue-100 text-blue-800' }
    }
    const info = statusMap[status] || statusMap.draft
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${info.color}`}>
        {info.label}
      </span>
    )
  }

  const handleExportPDF = async () => {
    if (!printContainerRef.current || !report) return

    try {
      toast.loading('Đang tạo file PDF...')

      // Temporarily hide no-print elements for PDF export
      const noPrintElements = document.querySelectorAll('.no-print')
      noPrintElements.forEach((el) => {
        (el as HTMLElement).style.display = 'none'
      })

      const opt = {
        margin: [5, 5, 5, 5] as [number, number, number, number], // top, right, bottom, left
        filename: `PhieuKetQua_CYT-${report.id}_${report.patient_name}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.92 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          allowTaint: true,
          scrollY: 0,
          scrollX: 0,
          windowWidth: document.documentElement.scrollWidth,
          windowHeight: document.documentElement.scrollHeight
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait' as const,
          compress: true
        },
        pagebreak: {
          mode: [],
          before: '',
          after: '',
          avoid: ''
        },
        enableLinks: false
      }

      await html2pdf().set(opt).from(printContainerRef.current).save()

      // Restore no-print elements
      noPrintElements.forEach((el) => {
        (el as HTMLElement).style.display = ''
      })

      toast.dismiss()
      toast.success('Đã tải xuống file PDF')
    } catch (error) {
      console.error('Error exporting PDF:', error)
      // Restore no-print elements even on error
      const noPrintElements = document.querySelectorAll('.no-print')
      noPrintElements.forEach((el) => {
        (el as HTMLElement).style.display = ''
      })
      toast.dismiss()
      toast.error('Không thể tạo file PDF')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="text-center text-gray-500">
          <p>Không tìm thấy phiếu kết quả</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <style>{`
        /* Page break control for PDF export */
        .page-break-before {
          page-break-before: always !important;
          break-before: page !important;
        }

        .page-break-after {
          page-break-after: always !important;
          break-after: page !important;
        }

        .page-break-avoid {
          page-break-inside: avoid !important;
          break-inside: avoid-page !important;
          overflow: visible !important;
          position: relative !important;
        }

        .bethesda-result-box {
          page-break-inside: avoid !important;
          break-inside: avoid-page !important;
          margin-bottom: 20px !important;
        }

        /* Prevent grid from breaking across pages */
        .grid {
          page-break-inside: auto !important;
        }

        .grid > div {
          page-break-inside: avoid !important;
          break-inside: avoid-page !important;
        }

        .bethesda-box-title {
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
        }

        @media print {
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 210mm;
            height: 297mm;
          }

          .no-print {
            display: none !important;
          }

          .print-container {
            display: block !important;
            position: relative !important;
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 3mm 5mm !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            background: white !important;
            font-size: 7px !important;
            line-height: 1.1 !important;
          }

          .page-break-before {
            page-break-before: always !important;
            break-before: page !important;
          }

          .page-break-after {
            page-break-after: always !important;
            break-after: page !important;
          }

          .page-break-avoid {
            page-break-inside: avoid !important;
            break-inside: avoid-page !important;
            overflow: visible !important;
          }

          .bethesda-result-box {
            page-break-inside: avoid !important;
            break-inside: avoid-page !important;
            margin-bottom: 20px !important;
          }

          h3, h4, h5 {
            page-break-after: avoid !important;
            break-after: avoid-page !important;
          }

          /* Prevent grid from breaking across pages in print */
          .grid {
            page-break-inside: auto !important;
          }

          .grid > div {
            page-break-inside: avoid !important;
            break-inside: avoid-page !important;
          }

          .bethesda-box-title {
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gray-50 p-6">
        {/* Action Bar - Hide when printing */}
        <div className="max-w-5xl mx-auto mb-6 flex justify-between items-center no-print">
          <button
            onClick={() => navigate('/pathology/reports')}
            className="text-gray-600 hover:text-gray-800 flex items-center"
          >
            ← Quay lại
          </button>
          <div className="flex items-center space-x-3">
            {getStatusBadge(report.status)}
            {/* View images button */}
            {report.report_images && report.report_images.length > 0 && (
              <Link
                to={`/imaging/viewer/${report.report_images[0].slide}`}
                className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 flex items-center space-x-2"
              >
                <span>✏️</span>
                <span>Xem ảnh</span>
              </Link>
            )}
            {/* Export PDF button */}
            <button
              onClick={handleExportPDF}
              className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
            >
              <span>📄</span>
              <span>Xuất PDF</span>
            </button>
          </div>
        </div>

        {/* Print Container - Only this will be printed */}
        <div ref={printContainerRef} className="bg-white p-2 rounded-lg shadow-lg max-w-5xl mx-auto print-container">
        {/* Header */}
        <div className="border-b border-gray-800 pb-0.5 mb-0.5">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                {logoUrl.startsWith('http') ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain rounded" />
                ) : (
                  <span className="text-sm">{logoUrl}</span>
                )}
              </div>
              <div>
                <h2 className="text-[8px] font-bold text-green-700 uppercase">{hospitalName}</h2>
                <p className="text-[7px] text-gray-600">{t('cytology.address')}: {hospitalAddress}</p>
              </div>
            </div>
          </div>

          <h1 className="text-center text-[10px] font-bold text-gray-900 mt-0.5 uppercase">
            {headerText}
          </h1>
        </div>

        {/* Patient Information */}
        <div className="mb-0.5 text-[8px] space-y-0 border-b border-gray-300 pb-0.5">
          {/* Line 1: Name, Age, Gender, Code */}
          <div>
            <span className="font-semibold">{t('cytology.patientName')}: </span>
            <span className="font-bold uppercase">{report.patient_name}</span>
            <span className="ml-2 font-semibold">{t('cytology.age')}: </span>
            <span>{report.age}</span>
            <span className="ml-2 font-semibold">{t('cytology.gender')}: </span>
            <span>{report.gender}</span>
            <span className="ml-2 font-semibold">Mã: </span>
            <span>{report.patient_code}</span>
          </div>

          {/* Line 2: Address */}
          <div>
            <span className="font-semibold">{t('cytology.address')}: </span>
            <span>{report.address || 'N/A'}</span>
            {report.province && <span> Tỉnh {report.province}</span>}
          </div>

          {/* Line 3: Department and Doctor */}
          <div>
            <span className="font-semibold">{t('cytology.department')}: </span>
            <span>{report.department || 'N/A'}</span>
            <span className="ml-4 font-semibold">{t('cytology.treatingDoctor')}: </span>
            <span>{report.treating_doctor || 'N/A'}</span>
          </div>

          {/* Line 4: Clinical Diagnosis */}
          <div>
            <span className="font-semibold">{t('cytology.clinicalDiagnosis')}: </span>
            <span>{report.clinical_diagnosis || 'N/A'}</span>
          </div>

          {/* Line 5: Specimen Type */}
          <div>
            <span className="font-semibold">{t('cytology.specimenType')}: </span>
            <input type="checkbox" className="mr-1 align-middle w-2.5 h-2.5" checked={report.specimen_type === 'Phết nhúng dịch'} disabled />
            <span>Phết nhúng dịch</span>
          </div>
        </div>

        {/* Bethesda 2014 Results */}
        <div className="border-t border-gray-400 pt-0.5 mb-0.5">
          <h3 className="text-center text-[9px] font-bold mb-0.5 text-blue-900 uppercase">
            {t('cytology.bethesdaTitle')}
          </h3>

          <div className="grid grid-cols-2 gap-1 text-[7px] page-break-avoid">
            {/* Organisms */}
            <div className="border-l-2 border-green-600 pl-1 page-break-avoid">
              <h4 className="font-bold text-green-700 mb-0.5 text-[8px] underline bethesda-box-title">{t('cytology.organismsTitle')}</h4>
              <div className="space-y-0">
                {Object.entries({
                  trichomonas: t('cytology.trichomonas'),
                  candida: t('cytology.candida'),
                  bacterialVaginosis: t('cytology.bacterialVaginosis'),
                  actinomyces: t('cytology.actinomyces'),
                  hsv: t('cytology.hsv'),
                  hpv: t('cytology.hpv')
                }).map(([key, label]) => (
                  <label key={key} className="flex items-center space-x-0.5">
                    <input
                      type="checkbox"
                      checked={report.organisms[key] || false}
                      disabled
                      className="w-2 h-2 text-green-600"
                    />
                    <span className="text-[7px] leading-tight">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Squamous Cells */}
            <div className="border-l-2 border-red-600 pl-1 page-break-avoid">
              <h4 className="font-bold text-red-700 mb-0.5 text-[8px] underline bethesda-box-title">{t('cytology.squamousCellsTitle')}</h4>
              <div className="space-y-0">
                {Object.entries({
                  ascus: t('cytology.ascus'),
                  asch: t('cytology.asch'),
                  lsil: t('cytology.lsil'),
                  lsilHpv: t('cytology.lsilHpv'),
                  hsil: t('cytology.hsil'),
                  squamousCarcinoma: t('cytology.squamousCarcinoma')
                }).map(([key, label]) => (
                  <label key={key} className="flex items-center space-x-0.5">
                    <input
                      type="checkbox"
                      checked={report.squamous_cells[key] || false}
                      disabled
                      className="w-2 h-2 text-red-600"
                    />
                    <span className="text-[7px] leading-tight">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Other Findings */}
            <div className="border-l-2 border-blue-600 pl-1 page-break-avoid">
              <h4 className="font-bold text-blue-700 mb-0.5 text-[8px] underline bethesda-box-title">{t('cytology.otherFindingsTitle')}</h4>
              <div className="space-y-0">
                {Object.entries({
                  normalCells: t('cytology.normalCells'),
                  inflammation: t('cytology.inflammation'),
                  radiation: t('cytology.radiation'),
                  iud: t('cytology.iud'),
                  atrophy: t('cytology.atrophy'),
                  postHysterectomy: t('cytology.postHysterectomy'),
                  endometrialCells: t('cytology.endometrialCells')
                }).map(([key, label]) => (
                  <label key={key} className="flex items-center space-x-0.5">
                    <input
                      type="checkbox"
                      checked={report.other_findings[key] || false}
                      disabled
                      className="w-2 h-2 text-blue-600"
                    />
                    <span className="text-[7px] leading-tight">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Glandular Cells */}
            <div className="border-l-2 border-purple-600 pl-1 page-break-avoid">
              <h4 className="font-bold text-purple-700 mb-0.5 text-[8px] underline bethesda-box-title">{t('cytology.glandularCellsTitle')}</h4>
              <div className="space-y-0">
                {Object.entries({
                  agc: t('cytology.agc'),
                  ais: t('cytology.ais'),
                  endocervicalAdenocarcinoma: t('cytology.endocervicalAdenocarcinoma'),
                  endometrialAdenocarcinoma: t('cytology.endometrialAdenocarcinoma'),
                  adenocarcinomaNos: t('cytology.adenocarcinomaNos'),
                  adenocarcinomaExtrauterine: t('cytology.adenocarcinomaExtrauterine')
                }).map(([key, label]) => (
                  <label key={key} className="flex items-center space-x-0.5">
                    <input
                      type="checkbox"
                      checked={report.glandular_cells[key] || false}
                      disabled
                      className="w-2 h-2 text-purple-600"
                    />
                    <span className="text-[7px] leading-tight">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Microscope Images Area - Between Bethesda boxes */}
          {report.report_images && report.report_images.length > 0 && (
            <div className="grid grid-cols-2 gap-0.5 mt-0.5">
              {report.report_images.slice(0, 2).map((img) => (
                <div key={img.id} className="border border-gray-300 p-0 bg-gray-50">
                  {img.snapshot_id && img.snapshot_image_url ? (
                    <img
                      src={img.snapshot_image_url}
                      alt="Microscope view"
                      className="w-full h-14 object-contain"
                    />
                  ) : (
                    <div className="w-full h-14 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center text-[7px] text-gray-500">
                      Microscope Image
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Conclusion */}
        <div className="mb-0.5 page-break-avoid">
          <h4 className="font-bold text-[9px] mb-0 underline">{t('cytology.conclusion')}:</h4>
          <div className="text-[8px] font-semibold uppercase whitespace-pre-wrap leading-tight">
            {report.conclusion}
          </div>
        </div>

        {/* Recommendation */}
        {report.recommendation && (
          <div className="mb-0.5 page-break-avoid">
            <h4 className="font-bold text-[9px] mb-0 underline">{t('cytology.recommendation')}:</h4>
            <div className="text-[8px] whitespace-pre-wrap leading-tight">
              {report.recommendation}
            </div>
          </div>
        )}

        {/* Selected Images with Annotations - Hidden in PDF */}
        {report.report_images && report.report_images.length > 0 && (
          <div className="mb-6 border-t pt-4 page-break-before no-print">
            <h3 className="font-bold text-lg mb-3">🖼️ Hình ảnh đính kèm ({report.report_images.length})</h3>
            <div className="grid grid-cols-2 gap-4 page-break-avoid">
              {report.report_images.map((img) => (
                <div key={img.id} className="border-2 border-purple-300 rounded-lg p-4 hover:shadow-lg transition-shadow">
                  {/* Render snapshot if available, otherwise annotation */}
                  {img.snapshot_id && img.snapshot_image_url ? (
                    <div className="bg-white rounded mb-2 border border-gray-200 overflow-hidden">
                      <div className="relative">
                        <img
                          src={img.snapshot_image_url}
                          alt="Snapshot"
                          className="w-full h-64 object-contain bg-gray-50"
                        />
                        {/* Snapshot badge */}
                        <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                          📸 SNAPSHOT
                        </div>
                      </div>
                      {/* Snapshot info */}
                      <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50">
                        <p className="text-sm font-medium text-gray-800">
                          📸 Snapshot Image
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          ID: {img.snapshot_id.substring(0, 8)}...
                        </p>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={`/imaging/viewer/${img.slide}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      {img.annotation_data && img.annotation_data.svg_data ? (
                      <div className="bg-white rounded mb-2 border border-gray-200 overflow-hidden p-4">
                        {/* Annotation Visual Display - Simplified */}
                        <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden border-2 border-purple-400 p-4 relative">
                          {(() => {
                            const coords = img.annotation_data.coordinates;
                            const color = img.annotation_data.color || '#00ff00';

                            if (!coords) return (
                              <div className="h-64 flex items-center justify-center text-gray-500">
                                No coordinate data
                              </div>
                            );

                            // Draw a scaled version of the annotation for display
                            const displayW = 400;
                            const displayH = 300;

                            // Scale annotation to fit display nicely (centered, large)
                            const annotType = img.annotation_data.annotation_type;

                            return (
                              <div className="relative bg-white rounded shadow-inner" style={{ width: displayW, height: displayH, margin: '0 auto' }}>
                                <svg
                                  width={displayW}
                                  height={displayH}
                                  viewBox={`0 0 ${displayW} ${displayH}`}
                                  style={{ display: 'block' }}
                                >
                                  {/* Grid pattern */}
                                  <defs>
                                    <pattern id={`grid-${img.id}`} width="20" height="20" patternUnits="userSpaceOnUse">
                                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
                                    </pattern>
                                  </defs>
                                  <rect width={displayW} height={displayH} fill="url(#grid-${img.id})" />

                                  {/* Draw annotation shape centered and large */}
                                  {annotType === 'rectangle' && (
                                    <rect
                                      x={displayW * 0.2}
                                      y={displayH * 0.2}
                                      width={displayW * 0.6}
                                      height={displayH * 0.6}
                                      fill={`${color}33`}
                                      stroke={color}
                                      strokeWidth="4"
                                    />
                                  )}

                                  {annotType === 'circle' && (
                                    <circle
                                      cx={displayW / 2}
                                      cy={displayH / 2}
                                      r={Math.min(displayW, displayH) * 0.3}
                                      fill={`${color}33`}
                                      stroke={color}
                                      strokeWidth="4"
                                    />
                                  )}

                                  {/* Center crosshair */}
                                  <line x1={displayW/2 - 20} y1={displayH/2} x2={displayW/2 + 20} y2={displayH/2} stroke="#666" strokeWidth="1" strokeDasharray="2,2" />
                                  <line x1={displayW/2} y1={displayH/2 - 20} x2={displayW/2} y2={displayH/2 + 20} stroke="#666" strokeWidth="1" strokeDasharray="2,2" />
                                </svg>

                                {/* Zoom badge */}
                                <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                  20x
                                </div>

                                {/* Size label */}
                                <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                                  {Math.round(coords.width)}×{Math.round(coords.height)} μm
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* Annotation info */}
                        <div className="mt-3 text-center">
                          <span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            {img.annotation_data.annotation_type.toUpperCase()}
                          </span>
                          {img.annotation_data.coordinates && (
                            <p className="text-xs text-gray-500 mt-2">
                              Position: ({Math.round(img.annotation_data.coordinates.x)}, {Math.round(img.annotation_data.coordinates.y)})
                              {img.annotation_data.coordinates.width &&
                                ` • Size: ${Math.round(img.annotation_data.coordinates.width)}×${Math.round(img.annotation_data.coordinates.height)}`
                              }
                            </p>
                          )}
                        </div>

                        {img.annotation_data.text_content && (
                          <div className="mt-2 p-2 bg-yellow-50 text-xs italic border-l-4 border-yellow-400">
                            💬 "{img.annotation_data.text_content}"
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-100 rounded h-64 flex items-center justify-center mb-2">
                        <span className="text-6xl">🔬</span>
                      </div>
                    )}
                    <p className="text-sm font-medium text-gray-800 truncate">{img.slide_name}</p>
                    {img.annotation_data && (
                      <p className="text-xs text-purple-600 mt-1">
                        📌 {img.annotation_data.annotation_type} annotation
                        {img.annotation_data.description && `: ${img.annotation_data.description}`}
                      </p>
                    )}
                      <p className="text-xs text-blue-600 hover:underline mt-2">Xem slide →</p>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer - Signatures */}
        <div className="border-t border-gray-300 pt-0.5 mt-0.5">
          <div className="grid grid-cols-2 gap-2 text-xs">
            {/* Left side - QR Code */}
            <div className="text-center">
              <div className="mb-0.5">
                <div className="inline-block border border-gray-400 p-0.5">
                  <canvas ref={qrCanvasRef} className="w-14 h-14" />
                  <p className="text-[6px] mt-0.5">{t('cytology.scanQR')}</p>
                </div>
              </div>
              <p className="font-bold text-[7px]">{t('cytology.pathologyDepartment')}</p>
              <p className="font-bold text-blue-900 text-[7px]">{hospitalName}</p>
              <p className="text-[6px] mt-0.5">{t('cytology.technician')}</p>
            </div>

            {/* Right side - Cytopathologist */}
            <div className="text-center">
              <p className="text-[7px] italic mb-0.5">
                {new Date(report.report_date).toLocaleString('vi-VN', {
                  hour: '2-digit',
                  minute: '2-digit',
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
              <p className="font-bold mb-0.5 text-[7px]">{t('cytology.cytopathologist')}</p>
              <div className="text-center font-bold text-[8px]">
                {report.cytopathologist}
              </div>
              <div className="mt-3">
                <p className="text-[6px] text-gray-500">Sign-Area</p>
              </div>
            </div>
          </div>
        </div>

        {/* Metadata (not printed) */}
        <div className="mt-6 pt-4 border-t border-dashed border-gray-300 no-print">
          <div className="text-xs text-gray-500">
            <p>Mã phiếu: CYT-{report.id}</p>
            <p>Cập nhật lần cuối: {formatDate(report.updated_at)}</p>
            <p>Trạng thái: {getStatusBadge(report.status)}</p>
          </div>
        </div>
        </div> {/* End print-container */}
      </div> {/* End page wrapper */}
    </>
  )
}

export default CytologyReportViewer
